import { useEffect, useState } from 'react'
import * as Sentry from '@sentry/react'
import i18n from 'i18next'
import { useIsAdmin } from '@/hooks/utility/useIsAdmin'

import { initSentryWithCurrentLanguage } from '@/utils/initSentry'
import { isLocal } from '@/utils/global/env'

type FeedbackWidgetActor = {
  appendToDom: () => void
  removeFromDom: () => void
}

export const SentryFeedback = () => {
  const isAdmin = useIsAdmin()
  const [widget, setWidget] = useState<FeedbackWidgetActor | null>(null)

  useEffect(() => {
    let currentWidget: FeedbackWidgetActor | null = null

    const createWidget = () => {
      const feedback = Sentry.getFeedback()
      if (feedback) {
        currentWidget = feedback.createWidget()
        if (currentWidget) {
          currentWidget.appendToDom()
          setWidget(currentWidget)
        }
      }
    }

    // Only show feedback button for superAdmin users in non-local environments
    if (!isLocal && isAdmin) {
      try {
        createWidget()
      } catch (error) {
        console.error('Failed to initialize Sentry feedback widget:', error)
      }
    }

    // Listen for language changes
    const onLangChange = () => {
      if (currentWidget) {
        currentWidget.removeFromDom()
        currentWidget = null
        setWidget(null)
      }
      if (!isLocal) {
        initSentryWithCurrentLanguage({ widgetOnly: true })
        if (isAdmin) {
          createWidget()
        }
      }
    }
    i18n.on('languageChanged', onLangChange)

    // Clean up the widget when the component unmounts or when user changes
    return () => {
      if (currentWidget) {
        currentWidget.removeFromDom()
      }
      if (widget && widget !== currentWidget) {
        widget.removeFromDom()
      }
      if (!isAdmin) {
        setWidget(null)
      }
      i18n.off('languageChanged', onLangChange)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]) // Only depend on isAdmin, not widget

  // This is a utility component that doesn't render anything visible
  return null
}

export default SentryFeedback
